import React, { useRef, useState } from "react";
import DashboardReportViewPaged from "components/pages/report-generation/DashboardReportViewPaged";
import { Instant } from "@js-joda/core";
import { I, T } from "services/lang/LanguageService";
import { ContextMenu } from "primereact/contextmenu";
import useTabId from "hooks/dashboard/useTabId";
import useDashboardStore from "store/dashboard-store";
import { ProgressSpinner } from "primereact/progressspinner";
import DashboardTabGroup from "./DashboardTabGroup";
import Reorder from "./reorder/Reorder";
import { ReorderType } from "./reorder/DraggableItem";
import { ContextMenuItem } from "../layouts/interfaces";
import "./DashboardTab.scss";

interface DashboardTabProps {
  isReports?: boolean;
  tabIndex?: number;
  refTime?: Instant;
}

const DashboardTab = ({ isReports, tabIndex, refTime }: DashboardTabProps) => {
  const currentTabID = useTabId();
  const tabId = isReports && tabIndex != null ? tabIndex : currentTabID;
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const dashboard = useDashboardStore(state => state.dashboard);
  const getTab = useDashboardStore(state => state.getTab);
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);
  const refreshKey = useDashboardStore(state => state.refreshKey);

  const cm = useRef<ContextMenu>(null);
  const [currentContextMenuItems, setCurrentContextMenuItems] = useState<ContextMenuItem[]>();

  if (!dashboard) {
    return <ProgressSpinner />;
  }

  const currentTab = isReports ? dashboard.dashboardTabsJs[tabId] : getTab(tabId);
  if (!currentTab) {
    return <div>{T("message.tab-not-found")}</div>;
  }

  document.title = `DWEEN - ${I(currentTab.title)}`;

  const reportPageSize = isReports ? dashboard.reportOptions?.pageLayout?.format?.pageSize : undefined;

  const tabContent = (
    <div key={refreshKey}>
      {isEditMode && <ContextMenu model={currentContextMenuItems} ref={cm} />}
      {currentTab.componentGroups?.map(group => (
        <DashboardTabGroup
          key={group.id}
          group={group}
          tabId={tabId}
          onContextMenu={(e: React.SyntheticEvent<Element, Event>) => cm.current?.show(e)}
          setCurrentContextMenuItems={setCurrentContextMenuItems}
          isEditMode={isEditMode}
          reportPageSize={reportPageSize}
          refTime={refTime}
        />
      ))}
      {isGroupReorderEnabled && (
        <Reorder
          setReorderDialogVisibility={reorderConfig => setIsGroupReorderEnabled(reorderConfig.isVisible)}
          reorderDialogVisibility={isGroupReorderEnabled}
          reorderType={ReorderType.GROUPS}
          tabId={tabId}
        />
      )}
    </div>
  );

  const singleTabDashboard = {
    ...dashboard,
    dashboardTabsJs: [dashboard.dashboardTabsJs[tabId]],
  };
  const containerId = `dashboard-tab-preview-${tabId}`;

  if (isReports && reportPageSize) {
    return isEditMode ? (
      <div key="report-edit" className="report-edit-mode-container">
        <div className={`report-page ${reportPageSize}`}>{tabContent}</div>
      </div>
    ) : (
      <div className="tab">
        <div key="report-preview" className="dashboard-report-wrapper-paged">
          <div id={containerId} />
          <DashboardReportViewPaged
            refTime={refTime?.toString() ?? Instant.now().toString()}
            containerId={containerId}
            dashboardOverride={singleTabDashboard}
          />
        </div>
      </div>
    );
  }

  return tabContent;
};

export default DashboardTab;
